/* eslint-disable no-param-reassign */
import React, { useEffect, useRef, useState } from 'react';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Typography } from '@mui/material';
import { createFormMember, getNewFormMember } from 'src/shared/services/formMemberService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { toast } from 'react-toastify';
import { ArrowForwardIosSharp } from '@mui/icons-material';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import DynamicFormField from 'src/shared/components/form/dynamicFormField';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { getMemberById } from 'src/shared/services/memberService';
import { createFamilyConnections } from 'src/shared/services/familyService';
import { MemberServerErrorSummary } from './memberServerErrorSummary';
import { MemberAccordionWrapper } from './memberAccordionWrapper';
import { Member } from '../models/member';
import { FormItemResponse, FormMemberResponse } from '../models/formMemberResponse';
import { FormMemberPost, FormMemberPostItem } from '../models/formMemberPost';
import { MemberFamilyCreateModeSelector } from './memberFamilyCreateModeSelector';
import { FamilyConnectionRequest } from '../models/familyMember';
import { MemberAddProfileDialog } from './memberAddProfileDialog';

type MemberFamilyCreateNewModalProps = {
  modalOpen: boolean;
  mainMemberId: number;
  onSave: () => void;
  onClose: () => void;
  callback: () => void;
};

export const MemberFamilyCreateNewModal = (props: MemberFamilyCreateNewModalProps) => {
  const { modalOpen, onClose, onSave, callback, mainMemberId } = props;
  const { t } = useTranslation(['member', 'common']);

  const [loading, setIsLoading] = useState<boolean>(false);
  const [fieldsReady, setFieldsReady] = useState<boolean>(false);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [rowsAndColsOrder, setRowsAndColsOrder] = useState<{ cols: string[] }[] | null>(null);
  const [createdMemberId, setCreatedMemberId] = useState<string | null>(null);
  const [showProfileDialog, setShowProfileDialog] = useState<boolean>(false);

  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [skipValidation, setSkipValidation] = useState<boolean>(false);
  const [payingMember, setPayingMember] = useState<boolean | undefined>(undefined);
  const [mainMember, setMainMember] = useState<Member | null>(null);

  const isMounted = useIsMounted();

  const modalTop = useRef<HTMLDivElement>(null);
  const scrollToModalTop = () => modalTop.current && modalTop.current.scrollIntoView();

  const formMethods = useForm<FormMemberResponse>({
    defaultValues: {
      items: [{}],
    },
  });

  const { control, handleSubmit, reset } = formMethods;

  const { fields } = useFieldArray({
    control,
    name: 'items',
  });

  useEffect(() => {
    if (!modalOpen) {
      setPayingMember(undefined);
      setShowForm(false);
      setFieldsReady(false);
      setRowsAndColsOrder(null);
      setMainMember(null);
      setServerErrors([]);
      reset({ items: [{}] });
      return;
    }

    const fetchMainMember = async () => {
      setIsLoading(true);
      try {
        const res = await getMemberById(mainMemberId);
        if (isMounted()) {
          setMainMember(res.data);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
          setMainMember(null);
        }
      } finally {
        if (isMounted()) setIsLoading(false);
      }
    };

    fetchMainMember();
  }, [modalOpen, mainMemberId, isMounted, reset]);

  useEffect(() => {
    // If the main member already has autogiro, skip the mode selector step
    if (
      modalOpen &&
      mainMember &&
      payingMember === undefined &&
      mainMember.autogiroStatus !== null &&
      typeof mainMember.autogiroStatus !== 'undefined'
    ) {
      setPayingMember(true);
      setShowForm(true);
    }
  }, [modalOpen, mainMember, payingMember]);

  useEffect(() => {
    if (!showForm || payingMember === undefined || !mainMember) return;

    const fetchFormSettings = async () => {
      setIsLoading(true);
      const result = await getNewFormMember(payingMember, mainMember.localAss_id);
      if (isMounted()) {
        // const uniqueRowIds = new Set<number>();
        // // Sort by row and column, start by finding all unique rows
        // result.data.items
        //   .filter((object) => object.formKey !== null)
        //   .forEach((object) => {
        //     uniqueRowIds.add(object.formSetting.row);
        //   });

        // add default values for null
        result.data.items.map((item) => {
          const nullCheck = (val: any) => {
            if (val === null) {
              return typeof item.formValue === 'number' ? 0 : '';
            }
            return val;
          };

          return Object.assign(item, { value: nullCheck(item.formValue) });
        });

        // Step 1: Group items by groupID
        const groupMap = new Map<number, FormItemResponse[]>();

        result.data.items
          .filter((object) => object.formKey !== null)
          .forEach((object) => {
            const { groupID } = object.formSetting;
            const groupItems = groupMap.get(groupID) || [];
            groupItems.push(object);
            groupMap.set(groupID, groupItems);
          });

        const sortedFormItems: FormItemResponse[] = [];

        // Step 2: Sort within each group by row and col
        groupMap.forEach((items) => {
          const uniqueRowIds = new Set<number>();

          // Find all unique rows within the current group
          items.forEach((item) => {
            uniqueRowIds.add(item.formSetting.row);
          });

          // Sort items within the group by row and column
          Array.from(uniqueRowIds)
            .sort((a, b) => a - b)
            .forEach((rowId) => {
              const colItems = items.filter((item) => item.formSetting.row === rowId).sort((a, b) => a.formSetting.col - b.formSetting.col);
              // Kontrollera om medlemmen har tillstånd att redigera adressuppgifter
              const isAdressLocked = true;
              colItems.forEach((child) => {
                // Lås adressuppgifter för familjmedlemmar utan behörighet
                if (
                  isAdressLocked &&
                  ['address', 'addressOther', 'postNr', 'postAddress', 'country', 'countyCode', 'municipalityCode'].some((ar) => ar === child.formKey)
                ) {
                  child.formSetting.locked = true;
                  child.formSetting.errorMessage = t('member:lockedFamilyMember');
                }
                sortedFormItems.push(child);
              });
            });
        });

        interface ColumnOrder {
          cols: string[];
        }

        // Step 3: Prepare the rows and columns structure for the form
        const mappedFormItems: ColumnOrder[] = [];
        let rowIndex = 0;
        let previousRow = 0;

        sortedFormItems.forEach((item) => {
          const { row } = item.formSetting;

          if (row !== previousRow) {
            rowIndex += 1;
          }

          const colItems = sortedFormItems.filter((itm) => itm.formSetting.row === row).map((itm) => itm.formKey);

          mappedFormItems[rowIndex] = { cols: colItems };
          previousRow = row;
        });

        setRowsAndColsOrder(mappedFormItems);
        const mainDefaults: Record<string, any> = {
          address: mainMember?.address,
          addressOther: mainMember?.addressOther,
          postNr: mainMember?.postNr,
          postAddress: mainMember?.postAddress,
          country: mainMember?.country,
          countyCode: mainMember?.countyCode,
          municipalityCode: mainMember?.municipalityCode,
          localAss_id: mainMember?.localAss_id ? `${mainMember.localAss_id}` : undefined,
          district: mainMember?.district?.name,
          districtCode: mainMember?.district?.code,
          countyAss_id: mainMember?.countyAss_id ?? mainMember?.district?.countyAss_id,
        };

        const prefilledFormItems = sortedFormItems.map((item) => {
          const inheritedValue = mainDefaults[item.formKey];
          if (inheritedValue !== undefined && (item.value === '' || item.value === null || typeof item.value === 'undefined')) {
            return { ...item, value: inheritedValue, formValue: inheritedValue };
          }
          return item;
        });

        reset({ items: prefilledFormItems });
        setFieldsReady(true);
        setIsLoading(false);
      }
    };

    if (payingMember !== undefined && mainMember?.localAss_id && showForm) fetchFormSettings();
  }, [reset, isMounted, t, mainMember, payingMember, showForm]);

  const onSubmit = React.useCallback(
    async (data: FormMemberResponse) => {
      setIsLoading(true);
      const formMemberPutItems: FormMemberPostItem[] = [];

      data.items.forEach((item) => {
        formMemberPutItems.push({
          formKey: item.formKey,
          formValue: Array.isArray(item.value) ? item.value.filter((x) => x !== null).join(',') : item.value,
        });
      });

      const formData: FormMemberPost = {
        skipValidation,
        items: formMemberPutItems,
        paid: false,
      };
      // ConsoleHelper.log('formData to post', formData);
      try {
        const res = await createFormMember(formData);

        if (isMounted()) {
          const newMemberId = Number(res?.data);

          if (newMemberId && mainMember?.member_id) {
            const connection: FamilyConnectionRequest = {
              member_id: mainMember.member_id,
              familymember_id: newMemberId,
            };

            await createFamilyConnections([connection]);
            toast.success(<>{t('member:familyConnectionAddedSuccess')}</>);
          } else {
            toast.success(<>{t('member:editMember.saveSuccessMessage')}</>);
          }

          setCreatedMemberId(newMemberId as unknown as string);
          setShowProfileDialog(true);

          setServerErrors([]);
        }
      } catch (e: any) {
        if (isMounted()) {
          ConsoleHelper.log('saveFormDataError:', e);
          setServerErrors(e.data);
          scrollToModalTop();
        }
      } finally {
        if (isMounted()) setIsLoading(false);
      }
    },
    [skipValidation, isMounted, t, mainMember?.member_id],
  );

  const onHandleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const onHandleSave = React.useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const handleSkipValidation = () => {
    setSkipValidation(true);
  };

  // Posta på nytt om man som authority >= 4 satt skipValidation till true
  useEffect(() => {
    if (skipValidation) handleSubmit(onSubmit)();
  }, [skipValidation, handleSubmit, onSubmit]);
  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      dialogTitle={
        mainMember
          ? `Lägg till ny familjemedlem för ${mainMember.firstName} ${mainMember.lastName} (#${mainMember.memberNr})`
          : t('member:button.createNew')
      }
      //   dialogSubTitle={`${member.firstName} ${member.lastName}`}
      onClose={onHandleClose}
      onSave={onHandleSave}
      dialogContent={
        <Box sx={{ position: 'relative', minHeight: '10rem' }} ref={modalTop}>
          <MemberServerErrorSummary
            serverErrors={serverErrors}
            isMember
            overridePermission={PermissionEnum.MemberUpdateOverride}
            onClickOverride={() => handleSkipValidation()}
          />
          {/* <MemberEditFamilyMessage member={member} /> */}
          <LoadingOverlay loading={loading} />
          {/* {!loading && member && currentMember && member.memberUpdate_id !== null && (
            <MemberUpdateRequestedMessage memberUpdateId={member.memberUpdate_id} isMember={!!currentMember} callback={() => ConsoleHelper.log('')} />
          )} */}
          {!showForm && mainMember && mainMember.autogiroStatus === null && (
            <MemberFamilyCreateModeSelector payingMember={payingMember} payingMemberCallback={(x) => setPayingMember(x)} />
          )}
          {payingMember !== undefined && mainMember?.localAss_id && showForm && (
            <FormProvider {...formMethods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <MemberAccordionWrapper hasError={serverErrors.length > 0}>
                  <>
                    {fieldsReady &&
                      fields &&
                      rowsAndColsOrder &&
                      (() => {
                        const groupedFields = new Map<number, { groupName: string; items: FormItemResponse[] }>();

                        // Step 1: Group items by groupID
                        fields.forEach((field) => {
                          const { groupID } = field.formSetting;
                          const groupName = field.formSetting.groupName || 'Unnamed Group';

                          if (!groupedFields.has(groupID)) {
                            groupedFields.set(groupID, { groupName, items: [] });
                          }

                          groupedFields.get(groupID)?.items.push(field);
                        });

                        // Step 2: Render each group
                        return Array.from(groupedFields.entries()).map(([groupID, groupData], index) => {
                          const { groupName, items } = groupData;

                          // Group the items by row and column for rendering
                          const rows = items.reduce((acc, item) => {
                            const { row } = item.formSetting;
                            if (!acc[row]) acc[row] = [];
                            acc[row].push(item);
                            return acc;
                          }, {} as Record<number, FormItemResponse[]>);

                          return (
                            <Accordion key={groupID} disableGutters elevation={0} square defaultExpanded>
                              <AccordionSummary
                                expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />}
                                aria-controls={`panel-${index}-content`}
                                id={`panel-${index}-header`}
                              >
                                <Typography sx={{ fontWeight: 500 }}>{groupName}</Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                {Object.entries(rows).map(([row, rowItems]) => (
                                  <Box sx={{ my: 1, mx: 1, mb: 3 }} key={row}>
                                    <GridWrapper>
                                      {rowItems.map((rowItem) => {
                                        const currentIndex = fields.findIndex(
                                          (y) =>
                                            y.formKey === rowItem.formKey &&
                                            y.formSetting.row === rowItem.formSetting.row &&
                                            y.formSetting.col === rowItem.formSetting.col,
                                        );
                                        return (
                                          <DynamicFormField
                                            key={`${rowItem.formKey}-${rowItem.formSetting.row}-${rowItem.formSetting.col}`}
                                            formItem={rowItem}
                                            index={currentIndex}
                                          />
                                        );
                                      })}
                                    </GridWrapper>
                                  </Box>
                                ))}
                              </AccordionDetails>
                            </Accordion>
                          );
                        });
                      })()}
                  </>
                </MemberAccordionWrapper>
              </form>
            </FormProvider>
          )}
          {createdMemberId && (
            <MemberAddProfileDialog
              open={showProfileDialog}
              onClose={() => {
                setShowProfileDialog(false);
                callback();
              }}
              memberId={createdMemberId}
            />
          )}
        </Box>
      }
      dialogActions={
        <Box display='flex' justifyContent='space-between' sx={{ width: '100%' }}>
          <Box />

          <Box>
            <Button variant='outlined' color='primary' disableElevation onClick={onHandleClose} sx={{ mr: 1 }}>
              {t('common:button.cancel')}
            </Button>

            {!showForm && mainMember && mainMember.autogiroStatus === null && (
              <Button
                disableElevation
                variant='contained'
                color='primary'
                onClick={() => setShowForm(true)}
                disabled={payingMember === undefined || !mainMember?.localAss_id}
              >
                {t('common:button.continue')}
              </Button>
            )}
            {showForm && (
              <Button disableElevation variant='contained' color='primary' onClick={onHandleSave} disabled={loading}>
                {t('common:button.save')}
              </Button>
            )}
          </Box>
        </Box>
      }
    />
  );
};
